// src/components/PriceCalculator/PriceCalculator.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import AnimatedMascot from "../AnimatedMascot/AnimatedMascot";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Link as ScrollLink } from "react-scroll";
import { t as translate, fallbackLng } from "../../i18n";

/**
 * Hintalaskuri V2
 * -------------------------------------------------
 * - Projektityypit: WordPress-sivut / Verkkokauppa / Räätälöity palvelu
 * - Näyttää projektihinnan ja jatkuvat kulut erikseen
 * - Tukee fi/en/es
 * - Käyttää ensin globaaleja i18n-resursseja (resources)
 * - Fallbackit sisällä komponentissa
 */

// --------------------------------------------------
// Pieni util: hae arvo dot.path-polulla
// --------------------------------------------------
const getByDotPath = (obj, dotPath) => {
  if (!obj || !dotPath) return undefined;
  return dotPath.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

// --------------------------------------------------
// Pieni util: interpolointi {x} / {{x}}
// --------------------------------------------------
const interpolate = (str, vars) => {
  if (typeof str !== "string") return str;
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}|\{(\w+)\}/g, (match, k1, k2) => {
    const k = k1 || k2;
    return vars[k] !== undefined ? String(vars[k]) : match;
  });
};

// --------------------------------------------------
// Kevyt fallback i18n (pääosin globaali resources hoitaa)
// --------------------------------------------------
const LOCAL_PRICE_I18N = {
  fi: {
    title: "Laske suuntaa-antava hinta",
    lead: "Rakenna karkea arvio verkkoprojektillesi (yritys tai yksityinen). Projektihinta ja jatkuvat kulut näytetään erikseen.",
    recommended: "Suositeltu",
    common: { moreInfo: "Lisätiedot", close: "Sulje" },
    units: { year: "vuosi", month: "kk" },
    live: {
      project: "Projekti (kertaluonteinen)",
      recurring: "Jatkuvat kulut",
      selected: "Valitut kohdat",
    },
    sections: {
      projectLevel: { title: "Projektin taso", subtitle: "Valitse tavoitetaso." },
      platform: { title: "Alusta & sisältö", subtitle: "Toteutustapa ja sisältöjen käsittely." },
      brand: { title: "Brändi & ilme", subtitle: "Visuaaliset lisät." },
      growth: { title: "Kasvumoottori", subtitle: "SEO, analytiikka ja automaatiot." },
      tech: { title: "Tekniikka & perusasiat", subtitle: "Tietosuoja, tietoturva, suorituskyky." },
      recurring: { title: "Jatkuvat kulut (infra)", subtitle: "Hosting, SSL ja ylläpito." },
      summary: { title: "Yhteenveto" },
    },
    fields: {
      projectLevel: { label: "Projektin taso" },
      platform: { label: "Toteutustapa" },
      copy: { label: "Tekstit / copy" },
      images: { label: "Kuvat" },
      hosting: { label: "Hosting" },
      ssl: { label: "SSL / HTTPS" },
    },
    options: {
      project: {
        none: "Valitse taso",
        visibility: "Perussivut (yksityinen / yritys)",
        business: "WordPress-sivusto",
        growth: "Laajapaketti (React + backend)",
      },
      platform: {
        wordpress: "WordPress",
        react_spa: "React / SPA",
      },
      copy: {
        own: "Omat tekstit",
        assisted: "Tekstien hiominen",
        full: "Sisällöntuotanto",
      },
      images: {
        own: "Omat kuvat",
        ai: "AI-kuvat",
        photo: "Kuvaus + editointi",
      },
      hosting: {
        none: "Ei hostingia",
        stellar: "Stellar",
        stellarPlus: "Stellar Plus",
      },
      ssl: {
        free: "Ilmainen SSL (Let's Encrypt)",
        paid: "Maksettu SSL",
      },
    },
    checks: {
      logo: { title: "Logo", desc: "Käyttökelpoinen logo verkkoon, someen ja perusmateriaaleihin." },
      brandKit: { title: "Brändipaketti", desc: "Värit, typografia ja visuaalinen suunta." },
      seoBasic: { title: "SEO (perus)", desc: "Otsikot, metat, OG-tagit ja rakenne." },
      seoAdvanced: { title: "SEO (laaja)", desc: "Tekninen SEO ja syvempi optimointi." },
      ga4: { title: "GA4 + GTM + tapahtumat", desc: "Seuraa klikkauksia, lomakkeita ja CTA:ta." },
      conversions: { title: "Konversioseuranta", desc: "Määritellään liidit ja mitataan toimivuus." },
      automation: { title: "Automaatiot", desc: "Lomake → sähköposti/CRM/työnkulku." },
      privacyCookies: { title: "Tietosuoja & evästeet", desc: "Tietosuojasivu, evästeilmoitus, GDPR-perusasiat." },
      security: { title: "Tietoturva & varmistukset", desc: "Hardening, varmuuskopiot ja suojaus." },
      performance: { title: "Nopeutus & optimointi", desc: "Nopeus ja Core Web Vitals -parannukset." },
      themeToggle: { title: "Tumma / vaalea teema", desc: "Automaattinen tai käyttäjän valittava teema." },
      multilang: { title: "Monikielisyys", desc: "Kielivalikko ja sivurakenne useille kielille." },
      newsletter: { title: "Uutiskirje", desc: "Sähköpostilistan keruu ja integraatio." },
      socialMedia: { title: "Some-linkit / jakopainikkeet", desc: "Linkit somekanaviin ja jakopainikkeet." },
      maintenance: { title: "Ylläpito", desc: "Päivitykset, seuranta ja pienet korjaukset." },
    },
    info: {
      projectLevel: "Taso kuvaa lopputulosta ja laajuutta. Lisäpalikat lasketaan tämän päälle.",
      platform: "WordPress sopii hyvin sekä yrityksille että yksityisille sivuille. React/SPA sopii järjestelmämäisempiin tai räätälöidympiin ratkaisuihin.",
      copy: "Hyvät tekstit parantavat konversioita.",
      images: "AI-kuvat ovat nopeita. Kuvaus + editointi tukee premium-brändiä.",
      brandKit: "Brändipaketti määrittää värit, typografian ja käyttölinjat.",
      seoAdvanced: "Laaja SEO sisältää teknisempää optimointia ja indeksoinnin tarkistuksia.",
      automation: "Automaatiot voivat nostaa konversioita yllättävän paljon.",
      performance: "Optimointi voi sisältää kuvat, lazy loadingin ja buildin optimoinnin.",
      recurring: "Nämä eivät ole projektihintaa vaan jatkuvia kuluja (hosting, SSL, ylläpito).",
      hosting: "Valitse 'Ei hostingia', jos asiakkaalla on oma hosting valmiina.",
      ssl: "Ilmainen SSL riittää usein hyvin. Maksettu SSL voi olla hyödyllinen erityistapauksissa.",
    },
    summary: {
      noneSelected: "Ei valintoja",
      projectPriceLabel: "Projektihinta (kertaluonteinen)",
      recurringLabel: "Jatkuvat kulut",
      recurringNote: "Domain ostetaan yleensä erikseen (hinta riippuu domain-nimestä).",
      ctaLead: "Tämä on suuntaa-antava arvio. Saat tarkan tarjouksen, kun käydään tavoitteet läpi.",
      ctaButton: "Pyydä tarkka tarjous",
      ctaFootnote: "Et sitoudu mihinkään — saat ensin ehdotuksen.",
    },
  },

  en: {
    title: "Price calculator",
    lead: "Build a rough estimate for your web project (business or private use). Project cost and recurring costs are shown separately.",
    recommended: "Recommended",
    common: { moreInfo: "More info", close: "Close" },
    units: { year: "year", month: "month" },
    live: { project: "Project (one-time)", recurring: "Recurring costs", selected: "Selected items" },
    sections: {
      projectLevel: { title: "Project level", subtitle: "Choose target scope." },
      platform: { title: "Platform & content", subtitle: "Implementation method and content handling." },
      brand: { title: "Brand & visual identity", subtitle: "Optional visual add-ons." },
      growth: { title: "Growth engine", subtitle: "SEO, analytics and automation." },
      tech: { title: "Technical essentials", subtitle: "Privacy, security and performance." },
      recurring: { title: "Recurring costs (infrastructure)", subtitle: "Hosting, SSL and maintenance." },
      summary: { title: "Summary" },
    },
    fields: {
      projectLevel: { label: "Project level" },
      platform: { label: "Implementation method" },
      copy: { label: "Content / copywriting" },
      images: { label: "Images" },
      hosting: { label: "Hosting" },
      ssl: { label: "SSL / HTTPS" },
    },
    options: {
      project: { none: "Select a level", visibility: "Basic website", business: "Business website", growth: "Growth platform" },
      platform: { wordpress: "WordPress", react_spa: "React / SPA" },
      copy: { own: "Client provides content", assisted: "Content polishing", full: "Full content production" },
      images: { own: "Client images", ai: "AI-generated images", photo: "Photography + editing" },
      hosting: { none: "No hosting", stellar: "Stellar", stellarPlus: "Stellar Plus" },
      ssl: { free: "Free SSL (Let's Encrypt)", paid: "Paid SSL" },
    },
    checks: {
      logo: { title: "Logo", desc: "Usable logo for web and social media." },
      brandKit: { title: "Brand kit", desc: "Colors, typography and visual direction." },
      seoBasic: { title: "SEO (basic)", desc: "Headings, meta data and structure." },
      seoAdvanced: { title: "SEO (advanced)", desc: "Technical SEO and deeper optimization." },
      ga4: { title: "GA4 + GTM + events", desc: "Track clicks, forms and CTAs." },
      conversions: { title: "Conversion tracking", desc: "Define and measure lead actions." },
      automation: { title: "Automation", desc: "Form → email/CRM/workflow." },
      privacyCookies: { title: "Privacy & cookies", desc: "Privacy page, cookie notice and GDPR basics." },
      security: { title: "Security & backups", desc: "Hardening, backups and practical protection." },
      performance: { title: "Performance optimization", desc: "Speed improvements and CWV improvements." },
      themeToggle: { title: "Dark / light theme", desc: "Automatic or user-selectable theme." },
      multilang: { title: "Multilingual support", desc: "Language switcher and page structure." },
      newsletter: { title: "Newsletter", desc: "Email capture and integration." },
      socialMedia: { title: "Social links / share buttons", desc: "Social links and optional share buttons." },
      maintenance: { title: "Maintenance", desc: "Updates, monitoring and small fixes." },
    },
    info: {
      projectLevel: "The level defines the outcome and scope. Add-ons are calculated on top.",
      platform: "WordPress fits both business and private websites well. React/SPA fits more system-like or custom solutions.",
      copy: "Strong content improves conversions.",
      images: "AI images are quick. Photography supports a premium brand.",
      brandKit: "Brand kit defines colors, typography and usage rules.",
      seoAdvanced: "Includes deeper technical SEO and indexing checks.",
      automation: "Automation can improve conversion more than visual polish alone.",
      performance: "May include image handling, lazy loading and build optimization.",
      recurring: "These are recurring costs (hosting, SSL, maintenance), not project cost.",
      hosting: "Choose 'No hosting' if the client already has hosting.",
      ssl: "Free SSL is often enough. Paid SSL may matter in special cases.",
    },
    summary: {
      noneSelected: "No selections",
      projectPriceLabel: "Project price (one-time)",
      recurringLabel: "Recurring costs",
      recurringNote: "Domain is usually purchased separately.",
      ctaLead: "This is a rough estimate. You’ll get an exact quote after reviewing goals and content.",
      ctaButton: "Request a detailed quote",
      ctaFootnote: "No commitment — you’ll receive a proposal first.",
    },
  },

  es: {
    title: "Calculadora de precios",
    lead: "Crea una estimación orientativa para tu proyecto web (empresa o uso privado). El coste del proyecto y los costes recurrentes se muestran por separado.",
    recommended: "Recomendado",
    common: { moreInfo: "Más información", close: "Cerrar" },
    units: { year: "año", month: "mes" },
    live: { project: "Proyecto (pago único)", recurring: "Costes recurrentes", selected: "Elementos seleccionados" },
    sections: {
      projectLevel: { title: "Nivel del proyecto", subtitle: "Elige el alcance objetivo." },
      platform: { title: "Plataforma y contenido", subtitle: "Método de implementación y contenidos." },
      brand: { title: "Marca e identidad visual", subtitle: "Extras visuales opcionales." },
      growth: { title: "Motor de crecimiento", subtitle: "SEO, analítica y automatización." },
      tech: { title: "Aspectos técnicos esenciales", subtitle: "Privacidad, seguridad y rendimiento." },
      recurring: { title: "Costes recurrentes (infraestructura)", subtitle: "Hosting, SSL y mantenimiento." },
      summary: { title: "Resumen" },
    },
    fields: {
      projectLevel: { label: "Nivel del proyecto" },
      platform: { label: "Método de implementación" },
      copy: { label: "Contenido / copywriting" },
      images: { label: "Imágenes" },
      hosting: { label: "Hosting" },
      ssl: { label: "SSL / HTTPS" },
    },
    options: {
      project: { none: "Selecciona un nivel", visibility: "Web básica", business: "Web de negocio", growth: "Plataforma de crecimiento" },
      platform: { wordpress: "WordPress", react_spa: "React / SPA" },
      copy: { own: "Contenido del cliente", assisted: "Revisión de textos", full: "Producción completa de contenido" },
      images: { own: "Imágenes del cliente", ai: "Imágenes con IA", photo: "Fotografía + edición" },
      hosting: { none: "Sin hosting", stellar: "Stellar", stellarPlus: "Stellar Plus" },
      ssl: { free: "SSL gratuito (Let's Encrypt)", paid: "SSL de pago" },
    },
    checks: {
      logo: { title: "Logo", desc: "Logo funcional para web y redes sociales." },
      brandKit: { title: "Kit de marca", desc: "Colores, tipografía y dirección visual." },
      seoBasic: { title: "SEO (básico)", desc: "Títulos, metadatos y estructura." },
      seoAdvanced: { title: "SEO (avanzado)", desc: "SEO técnico y optimización profunda." },
      ga4: { title: "GA4 + GTM + eventos", desc: "Medición de clics, formularios y CTAs." },
      conversions: { title: "Seguimiento de conversiones", desc: "Definir y medir acciones de lead." },
      automation: { title: "Automatización", desc: "Formulario → email/CRM/flujo." },
      privacyCookies: { title: "Privacidad y cookies", desc: "Página de privacidad y aviso de cookies." },
      security: { title: "Seguridad y copias de seguridad", desc: "Hardening, backups y protección práctica." },
      performance: { title: "Optimización de rendimiento", desc: "Mejoras de velocidad y Core Web Vitals." },
      themeToggle: { title: "Tema claro / oscuro", desc: "Tema automático o seleccionable." },
      multilang: { title: "Soporte multilingüe", desc: "Selector de idioma y estructura." },
      newsletter: { title: "Newsletter", desc: "Captación de emails e integración." },
      socialMedia: { title: "Enlaces sociales / compartir", desc: "Enlaces sociales y botones de compartir." },
      maintenance: { title: "Mantenimiento", desc: "Actualizaciones, seguimiento y pequeños ajustes." },
    },
    info: {
      projectLevel: "El nivel define resultado y alcance. Los extras se suman encima.",
      platform: "WordPress encaja bien tanto para empresas como para clientes privados. React/SPA encaja en soluciones más tipo sistema o a medida.",
      copy: "Un buen contenido mejora la conversión.",
      images: "Las imágenes IA son rápidas. La fotografía refuerza una marca premium.",
      brandKit: "El kit de marca define colores, tipografía y reglas de uso.",
      seoAdvanced: "Incluye SEO técnico e indexación más profunda.",
      automation: "La automatización puede mejorar conversiones más que solo diseño.",
      performance: "Puede incluir imágenes, lazy loading y optimización del build.",
      recurring: "Estos son costes recurrentes, no el coste del proyecto.",
      hosting: "Elige 'Sin hosting' si el cliente ya tiene hosting.",
      ssl: "El SSL gratuito suele ser suficiente.",
    },
    summary: {
      noneSelected: "Sin selecciones",
      projectPriceLabel: "Precio del proyecto (pago único)",
      recurringLabel: "Costes recurrentes",
      recurringNote: "El dominio normalmente se compra por separado.",
      ctaLead: "Esta es una estimación orientativa. Recibirás un presupuesto exacto tras revisar objetivos y contenido.",
      ctaButton: "Solicitar presupuesto detallado",
      ctaFootnote: "Sin compromiso — primero recibirás una propuesta.",
    },
  },
};

// --------------------------------------------------
// GA4 helper
// --------------------------------------------------
const fireEvent = (name, params = {}) => {
  if (window.gtag) window.gtag("event", name, params);
};

// --------------------------------------------------
// HINNAT (säädä vapaasti)
// --------------------------------------------------
const PRICES = {
  // Projektitaso nyt palvelutyypeittäin
  base: {
    website: {
      visibility: 700,
      business: 1400,
      growth: 2200,
    },
    ecommerce: {
      visibility: 1490,
      business: 2690,
      growth: 4490,
    },
    custom: {
      visibility: 1400,
      business: 2900,
      growth: 5900,
    },
  },

  // Alusta (automaattinen valinta palvelutyypin mukaan)
  platform: {
    wordpress: 0,
    react_spa: 700,
  },

  // Sisältö
  copy: {
    own: 0,
    assisted: 200,
    full: 450,
  },

  // Kuvapolku
  images: {
    own: 0,
    ai: 100,
    photo: 390,
  },

  // Brändi
  brand: {
    logo: 250,
    brandKit: 290,
  },

  // Kasvumoottori
  growth: {
    seo_basic: 0,
    seo_advanced: 590,
    analytics_ga4: 150,
    conversions: 200,
    automation: 390,
  },

  // Tekniikka
  tech: {
    security: 150,
    performance: 250,
    themeToggle: 120,
    multilang: 290,
    newsletter: 190,
    social: 90,
    privacyCookies: 120,
  },

  // Ylläpito palvelutyypeittäin (kk)
  maintenanceMonthly: {
    website: 49,
    ecommerce: 89,
    custom: 149,
  },
};

// --------------------------------------------------
// JATKUVAT KULUT
// --------------------------------------------------
const RECURRING = {
  hosting: {
    none: { yearly: 0 },
    stellar: { yearly: 19.84 },
    stellarPlus: { yearly: 30.24 },
  },
  ssl: {
    free: { yearly: 0 },
    paid: { yearly: 5.2 },
  },
};

// --------------------------------------------------
// V2 helperit: päätuote / tasotekstit / ylläpitotekstit
// --------------------------------------------------
const getSolutionTypeTexts = (lang) => {
  const dict = {
    fi: {
      sectionTitle: "Mitä rakennetaan?",
      sectionSubtitle:
        "Valitse ensin päätuote. Laskuri mukauttaa tasot ja ylläpitotekstit valinnan mukaan.",
      fieldLabel: "Palvelutyyppi",
      info:
        "Verkkosivut = WordPress-sivusto (yritys tai yksityinen), Verkkokauppa = WooCommerce-kauppa, Räätälöity verkkopalvelu = React + backend -ratkaisu.",
      options: {
        website: "Verkkosivut (WordPress)",
        ecommerce: "Verkkokauppa (WooCommerce)",
        custom: "Räätälöity verkkopalvelu (React + backend)",
      },
      platformAutoNote:
        "Toteutustapa valitaan automaattisesti palvelutyypin mukaan (WordPress/WooCommerce tai React + backend).",
      platformNames: {
        website: "WordPress",
        ecommerce: "WordPress + WooCommerce",
        custom: "React / SPA + backend",
      },
    },
    en: {
      sectionTitle: "What are we building?",
      sectionSubtitle:
        "Choose the main product first. The calculator adjusts levels and maintenance wording based on your selection.",
      fieldLabel: "Service type",
      info:
       "Website = WordPress site (business or private use), Ecommerce = WooCommerce store, Custom web service = React + backend solution.",
      options: {
        website: "Website (WordPress, private / business)",
        ecommerce: "Ecommerce (WooCommerce)",
        custom: "Custom web service (React + backend)",
      },
      platformAutoNote:
        "Implementation method is selected automatically based on service type (WordPress/WooCommerce or React + backend).",
      platformNames: {
        website: "WordPress",
        ecommerce: "WordPress + WooCommerce",
        custom: "React / SPA + backend",
      },
    },
    es: {
      sectionTitle: "¿Qué vamos a construir?",
      sectionSubtitle:
        "Primero elige el producto principal. La calculadora ajusta niveles y mantenimiento según tu elección.",
      fieldLabel: "Tipo de servicio",
      info:
        "Web = WordPress (empresa o uso privado), Tienda online = WooCommerce, Servicio web a medida = React + backend.",
      options: {
        website: "Web (WordPress, privado / empresa)",
        ecommerce: "Tienda online (WooCommerce)",
        custom: "Servicio web a medida (React + backend)",
      },
      platformAutoNote:
        "El método de implementación se selecciona automáticamente según el tipo de servicio.",
      platformNames: {
        website: "WordPress",
        ecommerce: "WordPress + WooCommerce",
        custom: "React / SPA + backend",
      },
    },
  };

  return dict[lang] || dict.fi;
};

const getProjectLevelTextsBySolution = (lang, solutionType) => {
  const dict = {
    fi: {
      sectionTitle: "Projektin taso",
      sectionSubtitle:
        "Valitse tavoitetaso — tämä määrittää peruslaajuuden ennen lisäpalikoita.",
      fieldLabel: "Projektin taso",
      info:
        "Taso kuvaa lopputulosta ja laajuutta. Lisäpalikat, kuten laajempi SEO, kuvat, logo ja analytiikka, lasketaan tämän päälle. Perus-SEO kuuluu verkkosivutoteutukseen.",
      noneLabel: "Valitse taso",
      includesTitle: "Mitä tähän tasoon yleensä kuuluu?",
      includesNote:
        "Suuntaa-antava sisältö. Tarkka laajuus sovitaan tarjouksessa tavoitteiden, sisällön ja aikataulun mukaan.",
      optionsByType: {
        website: {
          visibility: "Startti (verkkosivut)",
          business: "Business (verkkosivut)",
          growth: "Kasvu (verkkosivut)",
        },
        ecommerce: {
          visibility: "Pieni verkkokauppa",
          business: "Kasvava verkkokauppa",
          growth: "Laajempi kauppa / integraatiot",
        },
        custom: {
          visibility: "MVP / perus",
          business: "Business-tason palvelu",
          growth: "Laaja järjestelmä / palvelu",
        },
         },
      includesByType: {
        website: {
          visibility: [
            "Selkeä perussivusto (esim. etusivu + ydinsivut)",
            "Responsiivinen toteutus (mobiili / desktop)",
            "Yhteydenottotapa (lomake tai selkeä CTA)",
            "Perusrakenne hakukoneita varten",
            "Julkaisukuntoinen kokonaisuus",
          ],
          business: [
            "Laajempi sivurakenne ja palveluesittelyt",
            "Konversiota tukeva rakenne (CTA:t, yhteydenottopolut)",
            "Sisältöjen ja sivuhierarkian viimeistely",
            "Perusmittauksen/analyytiikan valmius",
            "Parempi laajennettavuus jatkokehitykseen",
          ],
          growth: [
            "Laajempi kokonaisuus kasvutavoitteella",
            "Useampia konversiopisteitä ja laskeutumissivuajattelua",
            "Mittaus- ja optimointivalmiuden parempi pohja",
            "Teknistä viimeistelyä suorituskykyyn / skaalautuvuuteen",
            "Selkeä pohja jatkuvalle kehitykselle",
          ],
        },
        ecommerce: {
          visibility: [
            "Pienen verkkokaupan perusrakenne",
            "Tuotteiden ja kategorioiden perusasetukset",
            "Ostopolun perustoimivuus",
            "Responsiivinen toteutus",
            "Julkaisukuntoinen kaupan peruspaketti",
          ],
          business: [
            "Kasvavan kaupan rakenne ja laajempi sisältökokonaisuus",
            "Parempi ostopolun ja konversion huomiointi",
            "Tuote-/kategoriarakenteen viimeistely",
            "Mittausvalmiuden parantaminen",
            "Valmius laajempaan kehitykseen",
          ],
          growth: [
            "Laajempi verkkokauppakokonaisuus",
            "Konversio-optimointia tukeva rakenne",
            "Syvempää teknistä ja kaupallista viimeistelyä",
            "Parempi integraatio-/jatkokehitysvalmius",
            "Pohja skaalautuvalle kasvulle",
          ],
        },
        custom: {
          visibility: [
            "MVP/perusversio idean testaamiseen",
            "Keskeiset näkymät ja ydintoiminnallisuus",
            "Perusrakenne frontille + backendille",
            "Julkaisukelpoinen ensimmäinen versio",
            "Pohja seuraaville kehitysaskelille",
          ],
          business: [
            "Business-tason palvelun runko",
            "Useampia toiminnallisuuksia ja näkymiä",
            "Selkeämpi data- ja käyttöpolkurakenne",
            "Teknistä viimeistelyä tuotantokäyttöön",
            "Parempi jatkokehitys- ja ylläpitovalmius",
          ],
          growth: [
            "Laajempi järjestelmä-/palvelukokonaisuus",
            "Useita rooleja, näkymiä tai prosesseja",
            "Syvempi backend-logiikka / integraatiovalmius",
            "Skaalautuvuutta tukeva toteutusrakenne",
            "Selkeä pohja pitkäjänteiseen kehitykseen",
          ],
        },
      },
    },
    en: {
      sectionTitle: "Project level",
      sectionSubtitle:
        "Choose the target scope first — this sets the baseline before add-ons.",
      fieldLabel: "Project level",
      info:
        "The level defines the outcome and scope. Add-ons (SEO, imagery, logo, analytics, etc.) are added on top.",
      noneLabel: "Select a level",
      includesTitle: "What is usually included in this level?",
      includesNote:
        "Indicative scope. Final scope is defined in the quote based on goals, content and schedule.",
      optionsByType: {
        website: {
          visibility: "Starter website",
          business: "Business website",
          growth: "Growth website",
        },
        ecommerce: {
          visibility: "Small online store",
          business: "Growing online store",
          growth: "Advanced store / integrations",
        },
        custom: {
          visibility: "MVP / basic",
          business: "Business-level service",
          growth: "Large system / service",
        },
         },
      includesByType: {
        website: {
          visibility: [
            "Clear basic website structure (e.g. homepage + core pages)",
            "Responsive implementation (mobile / desktop)",
            "Contact path (form or clear CTA)",
            "Basic search-friendly structure",
            "Launch-ready package",
          ],
          business: [
            "Broader site structure and service pages",
            "Conversion-oriented structure (CTAs, contact flows)",
            "Refined content and page hierarchy",
            "Basic analytics readiness",
            "Better expandability for future work",
          ],
          growth: [
            "Broader website package with growth focus",
            "More conversion points / landing-page thinking",
            "Stronger measurement and optimization foundation",
            "Technical finishing for performance / scalability",
            "Clear base for continuous development",
          ],
        },
        ecommerce: {
          visibility: [
            "Small online store basic structure",
            "Basic product and category setup",
            "Core checkout flow functionality",
            "Responsive implementation",
            "Launch-ready store basics",
          ],
          business: [
            "Growing store structure and broader content scope",
            "Stronger checkout/conversion focus",
            "Refined product/category structure",
            "Improved measurement readiness",
            "Readiness for further expansion",
          ],
          growth: [
            "Larger ecommerce package",
            "Conversion-oriented store structure",
            "Deeper technical and commercial finishing",
            "Stronger integration / future development readiness",
            "Foundation for scalable growth",
          ],
        },
        custom: {
          visibility: [
            "MVP/basic version for testing the idea",
            "Core views and key functionality",
            "Basic front-end + back-end structure",
            "First production-ready version",
            "Foundation for next development steps",
          ],
          business: [
            "Business-level service foundation",
            "More features and views",
            "Clearer data and user-flow structure",
            "Technical finishing for production use",
            "Better maintenance and future development readiness",
          ],
          growth: [
            "Larger system/service package",
            "Multiple roles, views or processes",
            "Deeper backend logic / integration readiness",
            "Implementation structure supporting scalability",
            "Clear foundation for long-term development",
          ],
        },
      },
    },
    es: {
      sectionTitle: "Nivel del proyecto",
      sectionSubtitle:
        "Elige primero el alcance objetivo: define la base antes de los extras.",
      fieldLabel: "Nivel del proyecto",
      info:
        "El nivel define el resultado y el alcance. Los extras (SEO, imágenes, logo, analítica, etc.) se suman encima.",
      noneLabel: "Selecciona un nivel",
      includesTitle: "¿Qué suele incluir este nivel?",
      includesNote:
        "Alcance orientativo. El alcance final se define en el presupuesto según objetivos, contenido y calendario.",
      optionsByType: {
        website: {
          visibility: "Web básica (inicio)",
          business: "Web de negocio",
          growth: "Web de crecimiento",
        },
        ecommerce: {
          visibility: "Tienda online pequeña",
          business: "Tienda en crecimiento",
          growth: "Tienda avanzada / integraciones",
        },
        custom: {
          visibility: "MVP / básico",
          business: "Servicio nivel business",
          growth: "Sistema / servicio amplio",
        },
      },
      includesByType: {
        website: {
          visibility: [
            "Estructura web básica clara (inicio + páginas clave)",
            "Implementación responsive (móvil / escritorio)",
            "Vía de contacto (formulario o CTA claro)",
            "Estructura base para buscadores",
            "Conjunto listo para publicar",
          ],
          business: [
            "Estructura más amplia y páginas de servicios",
            "Estructura orientada a conversión (CTAs, contacto)",
            "Mejora de contenidos y jerarquía de páginas",
            "Base para analítica / medición",
            "Mejor base para ampliaciones futuras",
          ],
          growth: [
            "Paquete web más amplio con foco en crecimiento",
            "Más puntos de conversión / enfoque landing",
            "Base más sólida para medición y optimización",
            "Acabado técnico para rendimiento / escalabilidad",
            "Base clara para desarrollo continuo",
          ],
        },
        ecommerce: {
          visibility: [
            "Estructura base de tienda online pequeña",
            "Configuración básica de productos y categorías",
            "Funcionamiento básico del proceso de compra",
            "Implementación responsive",
            "Tienda base lista para publicar",
          ],
          business: [
            "Estructura de tienda en crecimiento y mayor alcance",
            "Más enfoque en compra/conversión",
            "Mejora de estructura de productos/categorías",
            "Mejor preparación para medición",
            "Preparada para ampliar después",
          ],
          growth: [
            "Paquete ecommerce más amplio",
            "Estructura enfocada a conversión",
            "Acabado técnico y comercial más profundo",
            "Mayor preparación para integraciones / evolución",
            "Base para crecimiento escalable",
          ],
        },
        custom: {
          visibility: [
            "MVP/versión básica para validar la idea",
            "Vistas clave y funcionalidad principal",
            "Estructura base front-end + back-end",
            "Primera versión lista para producción",
            "Base para siguientes fases de desarrollo",
          ],
          business: [
            "Base de servicio de nivel business",
            "Más funcionalidades y vistas",
            "Estructura de datos y flujos más clara",
            "Acabado técnico para uso en producción",
            "Mejor preparación para mantenimiento y evolución",
          ],
          growth: [
            "Paquete de sistema/servicio más amplio",
            "Varios roles, vistas o procesos",
            "Lógica backend más profunda / integraciones",
            "Estructura preparada para escalabilidad",
            "Base clara para desarrollo a largo plazo",
          ],
        },
      },
    },
  };

  const base = dict[lang] || dict.fi;

  return {
    ...base,
    options: base.optionsByType?.[solutionType] || base.optionsByType.website,
    includes: base.includesByType?.[solutionType] || base.includesByType?.website || {},
  };
};

const getMaintenanceTextsBySolution = (lang, solutionType) => {
  const dict = {
    fi: {
      website: {
        title: "Ylläpito (WordPress-sivut)",
        desc: "Ensimmäinen kuukausi tukea ja ylläpitoa sisältyy projektihintaan. Sen jälkeen ylläpito alkaen 49 €/kk: päivitykset, seuranta ja pienet korjaukset.",
      },
      ecommerce: {
        title: "Ylläpito (verkkokauppa)",
        desc: "Ensimmäinen kuukausi tukea ja ylläpitoa sisältyy projektihintaan. Sen jälkeen verkkokaupan ylläpito alkaen 89 €/kk: päivitykset, seuranta ja kaupan toimivuuden varmistus.",
      },
      custom: {
        title: "Tekninen ylläpito (React + backend)",
        desc: "Ensimmäinen kuukausi teknistä tukea sisältyy projektihintaan. Sen jälkeen React + backend -ylläpito alkaen 149 €/kk: julkaisut, valvonta, päivitykset ja pienet korjaukset.",
      },
    },
    en: {
      website: {
        title: "Maintenance (WordPress site)",
        desc: "Optional but recommended: updates, monitoring and small fixes (scope agreed separately).",
      },
      ecommerce: {
        title: "Maintenance (ecommerce)",
        desc: "Strongly recommended: updates, monitoring, small fixes and store reliability checks.",
      },
      custom: {
        title: "Technical maintenance (React + backend)",
        desc: "Recommended / often necessary without an in-house developer: deployments, monitoring, updates and small fixes.",
      },
    },
    es: {
      website: {
        title: "Mantenimiento (sitio WordPress)",
        desc: "Opcional pero recomendado: actualizaciones, seguimiento y pequeños ajustes.",
      },
      ecommerce: {
        title: "Mantenimiento (tienda online)",
        desc: "Muy recomendado: actualizaciones, seguimiento, pequeños ajustes y control de funcionamiento de la tienda.",
      },
      custom: {
        title: "Mantenimiento técnico (React + backend)",
        desc: "Recomendado / a menudo necesario sin desarrollador propio: despliegues, monitorización, actualizaciones y pequeños ajustes.",
      },
    },
  };

  const langDict = dict[lang] || dict.fi;
  return langDict[solutionType] || langDict.website;
};

const getAutoPlatformForSolution = (solutionType) =>
  solutionType === "custom" ? "react_spa" : "wordpress";

const PriceCalculator = ({ lang: langProp } = {}) => {
  // --------------------------------------------------
  // AOS init
  // --------------------------------------------------
  useEffect(() => {
    AOS.init({ duration: 650, easing: "ease-out-cubic", once: false });
  }, []);

  // --------------------------------------------------
  // Kielen tunnistus (prop -> <html lang> -> fallback)
  // --------------------------------------------------
  const [htmlLang, setHtmlLang] = useState(() => {
    if (typeof document === "undefined") return "fi";
    return document.documentElement.lang || "fi";
  });

  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return undefined;
    }

    const update = () => setHtmlLang(document.documentElement.lang || "fi");
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"],
    });

    return () => observer.disconnect();
  }, []);

  const lang = useMemo(() => {
    const base =
      typeof langProp === "string" && langProp ? langProp : htmlLang || fallbackLng || "fi";
    return String(base).toLowerCase().startsWith("en")
      ? "en"
      : String(base).toLowerCase().startsWith("es")
      ? "es"
      : "fi";
  }, [langProp, htmlLang]);

  // --------------------------------------------------
  // i18n helper
  // --------------------------------------------------
  const t = useCallback(
    (keyOrKeys, vars, fallbackText = "") => {
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];

      // 1) yhteinen i18n, joka huomioi myös WordPress-hallinnan sisällön.
      for (const key of keys) {
        const val = translate(key, lang, vars);
        if (typeof val === "string" && val !== key) return val;
      }

      // 2) komponentin fallback (vain priceCalculator.*)
      const local = LOCAL_PRICE_I18N[lang] || LOCAL_PRICE_I18N.fi;

      for (const key of keys) {
        if (!String(key).startsWith("priceCalculator.")) continue;
        const localKey = key.replace(/^priceCalculator\./, "");
        const val = getByDotPath(local, localKey);
        if (typeof val === "string") return interpolate(val, vars);
      }

      // 3) fallbackText tai viimeinen avain
      if (fallbackText) return interpolate(fallbackText, vars);
      return keys[0];
    },
    [lang]
  );

  // --------------------------------------------------
  // Rahamuotoilu
  // --------------------------------------------------
  const money = useCallback(
    (value, opts = {}) => {
      const decimals = opts.decimals ?? 0;
      const locale = lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "fi-FI";

      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(Number(value || 0));
      } catch (err) {
        return `${Number(value || 0).toFixed(decimals)} €`;
      }
    },
    [lang]
  );

  // --------------------------------------------------
  // Valinnat
  // --------------------------------------------------
  const [solutionType, setSolutionType] = useState("website"); // website | ecommerce | custom
  const [projectLevel, setProjectLevel] = useState("none"); // none | visibility | business | growth
  const [platform, setPlatform] = useState("wordpress"); // wordpress | react_spa (automaattinen)

  // Infra
  const [hostingOption, setHostingOption] = useState("none"); // none | stellar | stellarPlus
  const [sslOption, setSslOption] = useState("free"); // free | paid

  // Sisältö
  const [copyOption, setCopyOption] = useState("own"); // own | assisted | full
  const [imagesOption, setImagesOption] = useState("own"); // own | ai | photo

  // Brändi
  const [logoDesign, setLogoDesign] = useState(false);
  const [brandKit, setBrandKit] = useState(false);

  // Kasvu
  const [seoBasic] = useState(false);
  const [seoAdvanced, setSeoAdvanced] = useState(false);
  const [ga4, setGa4] = useState(false);
  const [conversions, setConversions] = useState(false);
  const [automation, setAutomation] = useState(false);

  // Tekniikka
  const [privacyCookies, setPrivacyCookies] = useState(false);
  const [multilang, setMultilang] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [socialMedia, setSocialMedia] = useState(false);
  const [security, setSecurity] = useState(false);
  const [performance, setPerformance] = useState(false);
  const [themeToggle, setThemeToggle] = useState(false);

  // Ylläpito
  const [maintenance, setMaintenance] = useState(false);

  // UI
  const [clickTrigger, setClickTrigger] = useState(0);
  const [openInfo, setOpenInfo] = useState(null); // "brand" | "seo" | "automation" | "performance" | null

  const handleClick = () => setClickTrigger((c) => c + 1);

  // --------------------------------------------------
  // Johdetut V2-tekstit
  // --------------------------------------------------
  const solutionTexts = useMemo(() => getSolutionTypeTexts(lang), [lang]);
  const levelTexts = useMemo(
    () => getProjectLevelTextsBySolution(lang, solutionType),
    [lang, solutionType]
  );
  const maintenanceTexts = useMemo(
    () => getMaintenanceTextsBySolution(lang, solutionType),
    [lang, solutionType]
  );

  const currentBasePriceMap = useMemo(
    () => PRICES.base?.[solutionType] || PRICES.base.website,
    [solutionType]
  );

  const maintenanceMonthlyPrice = useMemo(() => {
    const cfg = PRICES.maintenanceMonthly;
    if (typeof cfg === "number") return cfg;
    return cfg?.[solutionType] ?? cfg?.website ?? 0;
  }, [solutionType]);

  // --------------------------------------------------
  // Alusta lukitaan automaattisesti palvelutyypin mukaan
  // --------------------------------------------------
  useEffect(() => {
    const nextPlatform = getAutoPlatformForSolution(solutionType);
    setPlatform((prev) => (prev === nextPlatform ? prev : nextPlatform));
  }, [solutionType]);

  // --------------------------------------------------
  // Hintalaskenta: projektihinta
  // --------------------------------------------------
  const projectPrice = useMemo(() => {
    let p = 0;

    // Projektitaso (palvelutyypin mukaan)
    if (projectLevel !== "none") {
      p += currentBasePriceMap?.[projectLevel] || 0;
    }

    // Alusta + sisältö (vain jos taso valittu)
    if (projectLevel !== "none") {
      p += PRICES.platform[platform] || 0;
      p += PRICES.copy[copyOption] || 0;
      p += PRICES.images[imagesOption] || 0;
    }

    // Brändi
    if (logoDesign) p += PRICES.brand.logo;
    if (brandKit) p += PRICES.brand.brandKit;

    // Kasvu
    if (seoBasic) p += PRICES.growth.seo_basic;
    if (seoAdvanced) p += PRICES.growth.seo_advanced;
    if (ga4) p += PRICES.growth.analytics_ga4;
    if (conversions) p += PRICES.growth.conversions;
    if (automation) p += PRICES.growth.automation;

    // Tekniikka
    if (privacyCookies) p += PRICES.tech.privacyCookies;
    if (multilang) p += PRICES.tech.multilang;
    if (newsletter) p += PRICES.tech.newsletter;
    if (socialMedia) p += PRICES.tech.social;
    if (security) p += PRICES.tech.security;
    if (performance) p += PRICES.tech.performance;
    if (themeToggle) p += PRICES.tech.themeToggle;

    return p;
  }, [
    projectLevel,
    currentBasePriceMap,
    platform,
    copyOption,
    imagesOption,
    logoDesign,
    brandKit,
    seoBasic,
    seoAdvanced,
    ga4,
    conversions,
    automation,
    privacyCookies,
    multilang,
    newsletter,
    socialMedia,
    security,
    performance,
    themeToggle,
  ]);

  // --------------------------------------------------
  // Jatkuvat kulut
  // --------------------------------------------------
  const recurringYearly = useMemo(() => {
    const hosting = RECURRING.hosting[hostingOption]?.yearly ?? 0;
    const ssl = RECURRING.ssl[sslOption]?.yearly ?? 0;
    return hosting + ssl;
  }, [hostingOption, sslOption]);

  const recurringMonthly = useMemo(
    () => (maintenance ? maintenanceMonthlyPrice : 0),
    [maintenance, maintenanceMonthlyPrice]
  );

  // --------------------------------------------------
  // Label-helperit (hosting/ssl + hinta)
  // --------------------------------------------------
  const hostingLabel = useCallback(
    (key) => {
      const yearly = RECURRING.hosting[key]?.yearly ?? 0;
      return `${t(`priceCalculator.options.hosting.${key}`)} (${money(yearly, {
        decimals: yearly % 1 ? 2 : 0,
      })}/${t("priceCalculator.units.year")})`;
    },
    [t, money]
  );

  const sslLabel = useCallback(
    (key) => {
      const yearly = RECURRING.ssl[key]?.yearly ?? 0;
      return `${t(`priceCalculator.options.ssl.${key}`)} (${money(yearly, {
        decimals: yearly % 1 ? 2 : 0,
      })}/${t("priceCalculator.units.year")})`;
    },
    [t, money]
  );

  // --------------------------------------------------
  // Yhteenvetorivit
  // --------------------------------------------------
  const selectedServices = useCallback(() => {
    const arr = [];

    // Palvelutyyppi
    arr.push(`${solutionTexts.fieldLabel}: ${solutionTexts.options[solutionType]}`);

    // Projektitaso (palvelutyypin mukaan nimetty)
    if (projectLevel === "visibility") {
      arr.push(`${levelTexts.options.visibility} – ${money(currentBasePriceMap.visibility)}`);
    }
    if (projectLevel === "business") {
      arr.push(`${levelTexts.options.business} – ${money(currentBasePriceMap.business)}`);
    }
    if (projectLevel === "growth") {
      arr.push(`${levelTexts.options.growth} – ${money(currentBasePriceMap.growth)}`);
    }

    // Alusta + sisältö (vain jos taso valittu)
    if (projectLevel !== "none") {
      const platformLabel = solutionTexts.platformNames[solutionType];
      const platformPrice = PRICES.platform[platform] || 0;

      arr.push(
        platformPrice > 0
          ? `${platformLabel} – +${money(platformPrice)}`
          : `${platformLabel} – ${money(platformPrice)}`
      );

      if (copyOption === "assisted") {
        arr.push(`${t("priceCalculator.options.copy.assisted")} – ${money(PRICES.copy.assisted)}`);
      }
      if (copyOption === "full") {
        arr.push(`${t("priceCalculator.options.copy.full")} – ${money(PRICES.copy.full)}`);
      }

      if (imagesOption === "ai") {
        arr.push(`${t("priceCalculator.options.images.ai")} – ${money(PRICES.images.ai)}`);
      }
      if (imagesOption === "photo") {
        arr.push(`${t("priceCalculator.options.images.photo")} – ${money(PRICES.images.photo)}`);
      }
    }

    // Brändi
    if (logoDesign) arr.push(`${t("priceCalculator.checks.logo.title")} – ${money(PRICES.brand.logo)}`);
    if (brandKit) arr.push(`${t("priceCalculator.checks.brandKit.title")} – ${money(PRICES.brand.brandKit)}`);

    // Kasvu
    if (seoBasic) arr.push(`${t("priceCalculator.checks.seoBasic.title")} – ${money(PRICES.growth.seo_basic)}`);
    if (seoAdvanced) arr.push(`${t("priceCalculator.checks.seoAdvanced.title")} – ${money(PRICES.growth.seo_advanced)}`);
    if (ga4) arr.push(`${t("priceCalculator.checks.ga4.title")} – ${money(PRICES.growth.analytics_ga4)}`);
    if (conversions) arr.push(`${t("priceCalculator.checks.conversions.title")} – ${money(PRICES.growth.conversions)}`);
    if (automation) arr.push(`${t("priceCalculator.checks.automation.title")} – ${money(PRICES.growth.automation)}`);

    // Tekniikka
    if (privacyCookies) {
      arr.push(`${t("priceCalculator.checks.privacyCookies.title")} – ${money(PRICES.tech.privacyCookies)}`);
    }
    if (multilang) {
      arr.push(`${t("priceCalculator.checks.multilang.title")} – ${money(PRICES.tech.multilang)}`);
    }
    if (newsletter) {
      arr.push(`${t("priceCalculator.checks.newsletter.title")} – ${money(PRICES.tech.newsletter)}`);
    }
    if (socialMedia) {
      arr.push(
        `${t(
          ["priceCalculator.checks.socialMedia.title", "priceCalculator.checks.social.title"],
          null,
          "Some-linkit / jakopainikkeet"
        )} – ${money(PRICES.tech.social)}`
      );
    }
    if (security) {
      arr.push(`${t("priceCalculator.checks.security.title")} – ${money(PRICES.tech.security)}`);
    }
    if (performance) {
      arr.push(`${t("priceCalculator.checks.performance.title")} – ${money(PRICES.tech.performance)}`);
    }
    if (themeToggle) {
      arr.push(`${t("priceCalculator.checks.themeToggle.title")} – ${money(PRICES.tech.themeToggle)}`);
    }

    // Infra
    if (hostingOption !== "none") {
      arr.push(`${t("priceCalculator.fields.hosting.label")} – ${hostingLabel(hostingOption)}`);
    }
    if (sslOption === "paid") {
      arr.push(`${t("priceCalculator.fields.ssl.label")} – ${sslLabel(sslOption)}`);
    }

    if (maintenance) {
      arr.push(
        `${maintenanceTexts.title} – ${money(maintenanceMonthlyPrice)}/${t("priceCalculator.units.month")}`
      );
    }

    return arr;
  }, [
    solutionType,
    solutionTexts,
    levelTexts,
    projectLevel,
    currentBasePriceMap,
    platform,
    copyOption,
    imagesOption,
    logoDesign,
    brandKit,
    seoBasic,
    seoAdvanced,
    ga4,
    conversions,
    automation,
    privacyCookies,
    multilang,
    newsletter,
    socialMedia,
    security,
    performance,
    themeToggle,
    hostingOption,
    sslOption,
    maintenance,
    maintenanceTexts,
    maintenanceMonthlyPrice,
    t,
    money,
    hostingLabel,
    sslLabel,
  ]);

  const selectedCount = useMemo(() => selectedServices().length, [selectedServices]);

  // --------------------------------------------------
  // Tooltip-ikoni
  // --------------------------------------------------
  const infoIcon = (text) => {
    const isOpen = openInfo === text;

    return (
      <span className="relative ml-3 inline-flex items-center">
        <button
          type="button"
          aria-label={t("priceCalculator.common.moreInfo")}
          aria-expanded={isOpen}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setOpenInfo(isOpen ? null : text);
          }}
          className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </button>

        {isOpen && (
          <span className="absolute left-1/2 top-full z-[999] mt-3 w-[260px] max-w-[80vw] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-center text-xs leading-relaxed text-white shadow-lg">
            {text}
          </span>
        )}
      </span>
    );
  };

  // --------------------------------------------------
  // Pienet GA4 eventit (muutokset)
  // --------------------------------------------------
  useEffect(() => {
    if (clickTrigger > 0) {
      fireEvent("hinta_laskuri_muutos", {
        palvelutyyppi: solutionType,
        taso: projectLevel,
        alusta: platform,
        hosting: hostingOption,
        ssl: sslOption,
        kieli: lang,
      });
    }
  }, [solutionType, projectLevel, platform, hostingOption, sslOption, clickTrigger, lang]);

  // --------------------------------------------------
  // Visuaalinen osio-wrapper
  // --------------------------------------------------
  const SectionCard = ({ title, subtitle, children, recommended = false }) => (
    <div className="relative z-0 hover:z-30 focus-within:z-30 mb-6 p-4 md:p-5 rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-gray-900/30 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-base md:text-lg font-semibold text-black dark:text-white">{title}</h3>

        {recommended && (
          <span className="text-[11px] md:text-xs px-2.5 py-1 rounded-full bg-lime-500/15 text-lime-700 dark:text-lime-300 border border-lime-500/25">
            {t("priceCalculator.recommended")}
          </span>
        )}
      </div>

      {subtitle ? (
        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-4">{subtitle}</p>
      ) : null}

      {children}
    </div>
  );

  return (
   <div
   className="w-full flex flex-col lg:flex-row items-start lg:items-start justify-center lg:space-x-8 space-y-8 lg:space-y-0"
   style={{ overflowAnchor: "none" }}
 >
      {/* Pääkortti */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 shadow-xl border border-gray-200 dark:border-white/10 flex-1 max-w-4xl w-full mx-auto lg:mx-0">
        {/* Header */}
        <div className="mb-6">
          <h4 className="text-2xl md:text-3xl font-bold text-center mb-2 text-black dark:text-white">
            {t("priceCalculator.title")}
          </h4>
          <p className="text-center text-sm md:text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t("priceCalculator.lead")}
          </p>

          {/* Live-strip / mittarit */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900/40 p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("priceCalculator.live.project")}
              </p>
              <p className="text-xl font-bold text-black dark:text-lime-400">{money(projectPrice)}</p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900/40 p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("priceCalculator.live.recurring")}
              </p>
              <p className="text-sm font-semibold text-black dark:text-white">
                {money(recurringYearly, { decimals: recurringYearly % 1 ? 2 : 0 })}/
                {t("priceCalculator.units.year")}
                {recurringMonthly > 0 && (
                  <>
                    {" "}
                    + {money(recurringMonthly)}/{t("priceCalculator.units.month")}
                  </>
                )}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900/40 p-3 text-center">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("priceCalculator.live.selected")}
              </p>
              <p className="text-xl font-bold text-black dark:text-white">{selectedCount}</p>
            </div>
          </div>
        </div>

        {/* 1) Mitä rakennetaan? (uusi V2) */}
        <SectionCard
          title={solutionTexts.sectionTitle}
          subtitle={solutionTexts.sectionSubtitle}
        >
          <div className="mb-1">
            <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
              {solutionTexts.fieldLabel}
              {infoIcon(solutionTexts.info)}
            </label>

            <select
              value={solutionType}
              onChange={(e) => {
                const nextType = e.target.value;
                const nextPlatform = getAutoPlatformForSolution(nextType);

                setSolutionType(nextType);
                setPlatform(nextPlatform);

                handleClick();
                fireEvent("hinta_laskuri_palvelutyyppi_valittu", {
                  palvelutyyppi: nextType,
                  alusta: nextPlatform,
                  kieli: lang,
                });
              }}
              className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg text-black bg-white dark:bg-gray-100 text-base md:text-lg"
            >
              <option value="website">{solutionTexts.options.website}</option>
              <option value="ecommerce">{solutionTexts.options.ecommerce}</option>
              <option value="custom">{solutionTexts.options.custom}</option>
            </select>
          </div>
        </SectionCard>

        {/* 2) Projektitaso */}
        <SectionCard
          title={levelTexts.sectionTitle}
          subtitle={levelTexts.sectionSubtitle}
        >
          <div className="mb-1">
            <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
              {levelTexts.fieldLabel}
              {infoIcon(levelTexts.info)}
            </label>

            <select
              value={projectLevel}
              onChange={(e) => {
                setProjectLevel(e.target.value);
                handleClick();
                fireEvent("hinta_laskuri_taso_valittu", {
                  palvelutyyppi: solutionType,
                  taso: e.target.value,
                  kieli: lang,
                });
              }}
              className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg text-black bg-white dark:bg-gray-100 text-base md:text-lg"
            >
              <option value="none">{levelTexts.noneLabel}</option>
              <option value="visibility">
                {levelTexts.options.visibility} – {money(currentBasePriceMap.visibility)}
              </option>
              <option value="business">
                {levelTexts.options.business} – {money(currentBasePriceMap.business)}
              </option>
              <option value="growth">
                {levelTexts.options.growth} – {money(currentBasePriceMap.growth)}
              </option>
            </select>

             {projectLevel !== "none" && Array.isArray(levelTexts.includes?.[projectLevel]) && (
              <div className="mt-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900/40 p-4">
                <h4 className="text-sm md:text-base font-semibold text-black dark:text-white">
                  {levelTexts.includesTitle}
                </h4>

                <ul className="mt-2 list-disc list-inside space-y-1 text-sm md:text-base text-gray-700 dark:text-gray-200">
                  {levelTexts.includes[projectLevel].map((item, idx) => (
                    <li key={`${projectLevel}-include-${idx}`}>{item}</li>
                  ))}
                </ul>

                <p className="mt-3 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                  {levelTexts.includesNote}
                </p>
              </div>
            )}
          </div>
        </SectionCard>

        {/* 3) Alusta + sisältö (näkyy vasta kun taso valittu) */}
        {projectLevel !== "none" && (
          <SectionCard
            title={t(
              [
                "priceCalculator.sections.platformContent.title",
                "priceCalculator.sections.platform.title",
              ],
              null,
              "Alusta & sisältö"
            )}
            subtitle={t(
              [
                "priceCalculator.sections.platformContent.subtitle",
                "priceCalculator.sections.platform.subtitle",
              ],
              null,
              "Valitse toteutustapa ja sisältöjen käsittely."
            )}
          >
            {/* Alusta (automaattinen V2) */}
            <div className="mb-5">
              <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
                {t("priceCalculator.fields.platform.label")}
                {infoIcon(t("priceCalculator.info.platform"))}
              </label>

              <div className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-gray-100">
                <div className="text-black text-base md:text-lg font-medium">
                  {solutionTexts.platformNames[solutionType]}
                  {(PRICES.platform[platform] || 0) > 0 && (
                    <>
                      {" "}
                      – +{money(PRICES.platform[platform])}
                    </>
                  )}
                  {(PRICES.platform[platform] || 0) === 0 && (
                    <>
                      {" "}
                      – {money(PRICES.platform[platform])}
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs md:text-sm text-gray-500">
                  {solutionTexts.platformAutoNote}
                </p>
              </div>
            </div>

            {/* Tekstit */}
            <div className="mb-5">
              <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
                {t("priceCalculator.fields.copy.label")}
                {infoIcon(t("priceCalculator.info.copy"))}
              </label>

              <select
                value={copyOption}
                onChange={(e) => {
                  setCopyOption(e.target.value);
                  handleClick();
                  fireEvent("hinta_laskuri_tekstit_valittu", {
                    palvelutyyppi: solutionType,
                    tekstit: e.target.value,
                    kieli: lang,
                  });
                }}
                className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg text-black bg-white dark:bg-gray-100 text-base md:text-lg"
              >
                <option value="own">
                  {t("priceCalculator.options.copy.own")} – {money(PRICES.copy.own)}
                </option>
                <option value="assisted">
                  {t("priceCalculator.options.copy.assisted")} – {money(PRICES.copy.assisted)}
                </option>
                <option value="full">
                  {t("priceCalculator.options.copy.full")} – {money(PRICES.copy.full)}
                </option>
              </select>
            </div>

            {/* Kuvat */}
            <div className="mb-1">
              <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
                {t("priceCalculator.fields.images.label")}
                {infoIcon(t("priceCalculator.info.images"))}
              </label>

              <select
                value={imagesOption}
                onChange={(e) => {
                  setImagesOption(e.target.value);
                  handleClick();
                  fireEvent("hinta_laskuri_kuvat_valittu", {
                    palvelutyyppi: solutionType,
                    kuvat: e.target.value,
                    kieli: lang,
                  });
                }}
                className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg text-black bg-white dark:bg-gray-100 text-base md:text-lg"
              >
                <option value="own">
                  {t("priceCalculator.options.images.own")} – {money(PRICES.images.own)}
                </option>
                <option value="ai">
                  {t("priceCalculator.options.images.ai")} – {money(PRICES.images.ai)}
                </option>
                <option value="photo">
                  {t("priceCalculator.options.images.photo")} – {money(PRICES.images.photo)}
                </option>
              </select>
            </div>
          </SectionCard>
        )}

        {/* 4) Brändi & ilme */}
        <SectionCard
          title={t("priceCalculator.sections.brand.title")}
          subtitle={t("priceCalculator.sections.brand.subtitle")}
        >
          <div className="flex flex-col space-y-4 text-black dark:text-gray-200 text-sm md:text-lg">
            {/* Logo */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={logoDesign}
                  onChange={() => {
                    const next = !logoDesign;
                    setLogoDesign(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_logo", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.logo.title")} – {money(PRICES.brand.logo)}
                </span>
              </label>

              <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.logo.desc")}
              </p>
            </div>

            {/* Brand kit */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-2">
                <label className="inline-flex items-start">
                  <input
                    type="checkbox"
                    checked={brandKit}
                    onChange={() => {
                      const next = !brandKit;
                      setBrandKit(next);
                      handleClick();
                      fireEvent("hinta_laskuri_lisa_brandkit", {
                        palvelutyyppi: solutionType,
                        paalla: next,
                        kieli: lang,
                      });
                    }}
                    className="mr-2 mt-1"
                  />
                  <span>
                    {t("priceCalculator.checks.brandKit.title")} – {money(PRICES.brand.brandKit)}
                  </span>
                </label>

                <button
                  type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenInfo(openInfo === "brand" ? null : "brand");
                    }}
                  className="text-xs text-blue-500 dark:text-blue-300 hover:underline ml-3 whitespace-nowrap"
                >
                  {openInfo === "brand"
                    ? t("priceCalculator.common.close")
                    : t("priceCalculator.common.moreInfo")}
                </button>
              </div>

              <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.brandKit.desc")}
              </p>

              {openInfo === "brand" && (
                <div className="ml-6 mt-2 p-3 rounded-lg bg-gray-900/80 border border-gray-700 text-xs text-gray-200 leading-snug">
                  {t("priceCalculator.info.brandKit")}
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        {/* 5) Kasvumoottori */}
        <SectionCard
          title={t("priceCalculator.sections.growth.title")}
          subtitle={t("priceCalculator.sections.growth.subtitle")}
          recommended
        >
          <div className="flex flex-col space-y-4 text-black dark:text-gray-200 text-sm md:text-lg">

            {/* SEO laaja */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-2">
                <label className="inline-flex items-start">
                  <input
                    type="checkbox"
                    checked={seoAdvanced}
                    onChange={() => {
                      const next = !seoAdvanced;
                      setSeoAdvanced(next);

                      handleClick();
                      fireEvent("hinta_laskuri_lisa_seo_laaja", {
                        palvelutyyppi: solutionType,
                        paalla: next,
                        kieli: lang,
                      });
                    }}
                    className="mr-2 mt-1"
                  />
                  <span>
                    {t("priceCalculator.checks.seoAdvanced.title")} –{" "}
                    {money(PRICES.growth.seo_advanced)}
                  </span>
                </label>

                <button
                  type="button"
                  onClick={() => setOpenInfo(openInfo === "seo" ? null : "seo")}
                  className="text-xs text-blue-500 dark:text-blue-300 hover:underline ml-3 whitespace-nowrap"
                >
                  {openInfo === "seo"
                    ? t("priceCalculator.common.close")
                    : t("priceCalculator.common.moreInfo")}
                </button>
              </div>

              <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.seoAdvanced.desc")}
              </p>

              {openInfo === "seo" && (
                <div className="ml-6 mt-2 p-3 rounded-lg bg-gray-900/80 border border-gray-700 text-xs text-gray-200 leading-snug">
                  {t("priceCalculator.info.seoAdvanced")}
                </div>
              )}
            </div>

            {/* GA4 */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={ga4}
                  onChange={() => {
                    const next = !ga4;
                    setGa4(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_ga4", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.ga4.title")} – {money(PRICES.growth.analytics_ga4)}
                </span>
              </label>

              <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.ga4.desc")}
              </p>
            </div>

            {/* Konversiot */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={conversions}
                  onChange={() => {
                    const next = !conversions;
                    setConversions(next);

                    if (!next && automation) setAutomation(false);

                    handleClick();
                    fireEvent("hinta_laskuri_lisa_konversiot", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.conversions.title")} – {money(PRICES.growth.conversions)}
                </span>
              </label>

              <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.conversions.desc")}
              </p>
            </div>

            {/* Automaatiot */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-2">
                <label className="inline-flex items-start">
                  <input
                    type="checkbox"
                    checked={automation}
                    onChange={() => {
                      const next = !automation;
                      setAutomation(next);

                      if (next && !conversions) setConversions(true);

                      handleClick();
                      fireEvent("hinta_laskuri_lisa_automaatiot", {
                        palvelutyyppi: solutionType,
                        paalla: next,
                        kieli: lang,
                      });
                    }}
                    className="mr-2 mt-1"
                  />
                  <span>
                    {t("priceCalculator.checks.automation.title")} – {money(PRICES.growth.automation)}
                  </span>
                </label>

                <button
                  type="button"
                  onClick={() => setOpenInfo(openInfo === "automation" ? null : "automation")}
                  className="text-xs text-blue-500 dark:text-blue-300 hover:underline ml-3 whitespace-nowrap"
                >
                  {openInfo === "automation"
                    ? t("priceCalculator.common.close")
                    : t("priceCalculator.common.moreInfo")}
                </button>
              </div>

              <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.automation.desc")}
              </p>

              {openInfo === "automation" && (
                <div className="ml-6 mt-2 p-3 rounded-lg bg-gray-900/80 border border-gray-700 text-xs text-gray-200 leading-snug">
                  {t("priceCalculator.info.automation")}
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        {/* 6) Tekniikka & perusasiat */}
        <SectionCard
          title={t("priceCalculator.sections.tech.title")}
          subtitle={t("priceCalculator.sections.tech.subtitle")}
        >
          <div className="flex flex-col space-y-3 text-black dark:text-gray-200 text-sm md:text-lg">
            {/* Tietosuoja */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={privacyCookies}
                  onChange={() => {
                    const next = !privacyCookies;
                    setPrivacyCookies(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_tietosuoja", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.privacyCookies.title")} –{" "}
                  {money(PRICES.tech.privacyCookies)}
                </span>
              </label>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.privacyCookies.desc")}
              </span>
            </div>

            {/* Tietoturva */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={security}
                  onChange={() => {
                    const next = !security;
                    setSecurity(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_tietoturva", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.security.title")} – {money(PRICES.tech.security)}
                </span>
              </label>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.security.desc")}
              </span>
            </div>

            {/* Nopeutus */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-2">
                <label className="inline-flex items-start">
                  <input
                    type="checkbox"
                    checked={performance}
                    onChange={() => {
                      const next = !performance;
                      setPerformance(next);
                      handleClick();
                      fireEvent("hinta_laskuri_lisa_nopeutus", {
                        palvelutyyppi: solutionType,
                        paalla: next,
                        kieli: lang,
                      });
                    }}
                    className="mr-2 mt-1"
                  />
                  <span>
                    {t("priceCalculator.checks.performance.title")} – {money(PRICES.tech.performance)}
                  </span>
                </label>

                <button
                  type="button"
                  onClick={() => setOpenInfo(openInfo === "performance" ? null : "performance")}
                  className="text-xs text-blue-500 dark:text-blue-300 hover:underline ml-3 whitespace-nowrap"
                >
                  {openInfo === "performance"
                    ? t("priceCalculator.common.close")
                    : t("priceCalculator.common.moreInfo")}
                </button>
              </div>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.performance.desc")}
              </span>

              {openInfo === "performance" && (
                <div className="ml-6 mt-2 p-3 rounded-lg bg-gray-900/80 border border-gray-700 text-xs text-gray-200 leading-snug">
                  {t("priceCalculator.info.performance")}
                </div>
              )}
            </div>

            {/* Teema */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={themeToggle}
                  onChange={() => {
                    const next = !themeToggle;
                    setThemeToggle(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_teema", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.themeToggle.title")} – {money(PRICES.tech.themeToggle)}
                </span>
              </label>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.themeToggle.desc")}
              </span>
            </div>

            {/* Monikielisyys */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={multilang}
                  onChange={() => {
                    const next = !multilang;
                    setMultilang(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_kielet", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.multilang.title")} – {money(PRICES.tech.multilang)}
                </span>
              </label>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.multilang.desc")}
              </span>
            </div>

            {/* Uutiskirje */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={newsletter}
                  onChange={() => {
                    const next = !newsletter;
                    setNewsletter(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_uutiskirje", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t("priceCalculator.checks.newsletter.title")} – {money(PRICES.tech.newsletter)}
                </span>
              </label>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.checks.newsletter.desc")}
              </span>
            </div>

            {/* Some-linkit */}
            <div className="flex flex-col">
              <label className="inline-flex items-start">
                <input
                  type="checkbox"
                  checked={socialMedia}
                  onChange={() => {
                    const next = !socialMedia;
                    setSocialMedia(next);
                    handleClick();
                    fireEvent("hinta_laskuri_lisa_some", {
                      palvelutyyppi: solutionType,
                      paalla: next,
                      kieli: lang,
                    });
                  }}
                  className="mr-2 mt-1"
                />
                <span>
                  {t(
                    ["priceCalculator.checks.socialMedia.title", "priceCalculator.checks.social.title"],
                    null,
                    "Some-linkit / jakopainikkeet"
                  )}{" "}
                  – {money(PRICES.tech.social)}
                </span>
              </label>

              <span className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                {t(
                  ["priceCalculator.checks.socialMedia.desc", "priceCalculator.checks.social.desc"],
                  null,
                  "Linkit somekanaviin ja halutessa jakopainikkeet."
                )}
              </span>
            </div>
          </div>
        </SectionCard>

        {/* 7) Infra / jatkuvat kulut */}
        <SectionCard
          title={
            <span className="inline-flex items-center">
              {t("priceCalculator.sections.recurring.title")}
              {infoIcon(t("priceCalculator.info.recurring"))}
            </span>
          }
          subtitle={t("priceCalculator.sections.recurring.subtitle")}
        >
          {/* Hosting */}
          <div className="mb-4">
            <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
              {t("priceCalculator.fields.hosting.label")}
              {infoIcon(t("priceCalculator.info.hosting"))}
            </label>

            <select
              value={hostingOption}
              onChange={(e) => {
                setHostingOption(e.target.value);
                handleClick();
                fireEvent("hinta_laskuri_hosting_valittu", {
                  palvelutyyppi: solutionType,
                  hosting: e.target.value,
                  kieli: lang,
                });
              }}
              className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg text-black bg-white dark:bg-gray-100 text-base md:text-lg"
            >
              <option value="none">{hostingLabel("none")}</option>
              <option value="stellar">{hostingLabel("stellar")}</option>
              <option value="stellarPlus">{hostingLabel("stellarPlus")}</option>
            </select>
          </div>

          {/* SSL */}
          <div className="mb-2">
            <label className="block mb-2 text-base md:text-lg text-black dark:text-white">
              {t("priceCalculator.fields.ssl.label")}
              {infoIcon(t("priceCalculator.info.ssl"))}
            </label>

            <select
              value={sslOption}
              onChange={(e) => {
                setSslOption(e.target.value);
                handleClick();
                fireEvent("hinta_laskuri_ssl_valittu", {
                  palvelutyyppi: solutionType,
                  ssl: e.target.value,
                  kieli: lang,
                });
              }}
              className="w-full p-3 border border-gray-300 dark:border-white/10 rounded-lg text-black bg-white dark:bg-gray-100 text-base md:text-lg"
            >
              <option value="free">{sslLabel("free")}</option>
              <option value="paid">{sslLabel("paid")}</option>
            </select>
          </div>

          {/* Ylläpito */}
          <div className="mt-4">
            <label className="inline-flex items-start text-base md:text-lg text-black dark:text-white">
              <input
                type="checkbox"
                checked={maintenance}
                onChange={() => {
                  const next = !maintenance;
                  setMaintenance(next);
                  handleClick();
                  fireEvent("hinta_laskuri_yllapito", {
                    palvelutyyppi: solutionType,
                    paalla: next,
                    kieli: lang,
                  });
                }}
                className="mr-2 mt-1"
              />

              <span>
                {maintenanceTexts.title} – {money(maintenanceMonthlyPrice)}/
                {t("priceCalculator.units.month")}
                {infoIcon(maintenanceTexts.desc)}
              </span>
            </label>
          </div>
        </SectionCard>

        {/* 8) Yhteenveto */}
         <div
            data-aos="fade-up"
            className="mt-4"
            style={{ overflowAnchor: "none" }}
          >
          <div className="p-4 md:p-5 rounded-2xl border border-lime-500/30 bg-lime-500/5 dark:bg-lime-500/10">
            <h5 className="font-semibold mb-2 text-sm md:text-lg text-black dark:text-white">
              {t("priceCalculator.sections.summary.title")}
            </h5>

            {selectedCount ? (
              <ul className="list-disc list-inside mb-4 dark:text-lime-300 text-sm md:text-base space-y-1">
                {selectedServices().map((s, i) => (
                  <li key={`${s}-${i}`}>{s}</li>
                ))}
              </ul>
            ) : (
              <p className="dark:text-lime-300 font-bold text-base md:text-lg">
                {t("priceCalculator.summary.noneSelected")}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              {/* Projektihinta */}
              <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-black/20 p-4 text-center">
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">
                  {t("priceCalculator.summary.projectPriceLabel")}
                </p>
                <p className="text-3xl font-bold dark:text-lime-400 text-black">
                  {money(projectPrice)}
                </p>
              </div>

              {/* Jatkuvat kulut */}
              <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-black/20 p-4 text-center">
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">
                  {t("priceCalculator.summary.recurringLabel")}
                </p>

                <p className="text-lg font-semibold text-black dark:text-white">
                  {money(recurringYearly, { decimals: recurringYearly % 1 ? 2 : 0 })}/
                  {t("priceCalculator.units.year")}
                  {recurringMonthly > 0 && (
                    <>
                      <br />
                      + {money(recurringMonthly)}/{t("priceCalculator.units.month")}
                    </>
                  )}
                </p>

                <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                  {t("priceCalculator.summary.recurringNote")}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-5 p-4 rounded-xl bg-white/80 dark:bg-black/20 border border-white/40 dark:border-white/10 text-center">
              <p className="text-sm md:text-base text-gray-700 dark:text-gray-200 mb-3">
                {t("priceCalculator.summary.ctaLead")}
              </p>

              <ScrollLink
                to="contact"
                smooth
                duration={600}
                offset={-96}
                onClick={() => {
                  fireEvent("hinta_laskuri_laheta_liidi", {
                    solutionType,
                    projectLevel,
                    platform,
                    price: projectPrice,
                    lang,
                  });
                }}
                className="inline-block px-6 py-3 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-semibold transition-all shadow-lg cursor-pointer"
              >
                {t("priceCalculator.summary.ctaButton")} →
              </ScrollLink>

              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t("priceCalculator.summary.ctaFootnote")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Maskotti (desktop) */}
      <div className="hidden lg:flex items-start justify-center pt-6">
        <div className="sticky top-28">
          <AnimatedMascot trigger={clickTrigger} />
        </div>
      </div>
    </div>
  );
};

export default PriceCalculator;